import { type JSX, useContext, useState } from 'react';

import type { Pilot, PilotRule } from '@schema/pilot.js';
import type { Tile } from '@schema/tiles.js';

import { PilotSubnetsContext } from '../pilot-subnets.js';
import { PilotTagsContext } from '../pilot-tags.js';
import { SelectField, ToggleField } from './Field.js';

/** Tiles that can carry a pilot (everything but spacers/dividers). */
type PilotableTile = Exclude<Tile, { type: 'spacer' } | { type: 'divider' }>;

interface TilePilotEditorProps {
  tile: PilotableTile;
  onChange: (next: PilotableTile) => void;
}

const DAY_MS = 86_400_000;
const PCT_OPTIONS = Array.from({ length: 21 }, (_, i) => {
  const v = String(i * 5);
  return { value: v, label: `${v}%` };
});

const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
const todayIso = (): string => `${isoDay(Date.now())}T00:00:00Z`;
const dateInputValue = (iso: string | undefined): string => (iso !== undefined ? iso.slice(0, 10) : '');
const toIso = (d: string): string | undefined => (d ? `${d}T00:00:00Z` : undefined);
const clamp = (n: number, lo: number, hi: number): number =>
  Number.isNaN(n) ? lo : Math.min(hi, Math.max(lo, n));

/**
 * Per-tile pilot (canary) targeting editor. Phase A surfaces the two
 * device-self-resolvable rule types — a static percentage and a daily ramp;
 * subnet + tag targeting (which need server-supplied device context) land in
 * a later phase. Devices ignore all of this unless the tenant is entitled to
 * `pilotRollout` (Enterprise).
 */
export function TilePilotEditor({ tile, onChange }: TilePilotEditorProps): JSX.Element {
  const subnets = useContext(PilotSubnetsContext);
  const tags = useContext(PilotTagsContext);
  const pilot = tile.pilot;

  const setPilot = (next: Pilot | undefined): void => {
    if (next === undefined) {
      const { pilot: _omit, ...rest } = tile;
      void _omit;
      onChange(rest as PilotableTile);
    } else {
      onChange({ ...tile, pilot: next } as PilotableTile);
    }
  };

  if (pilot === undefined) {
    return (
      <fieldset className="tile-card__pilot">
        <legend className="tile-card__appearance-label">Pilot rollout</legend>
        <ToggleField
          label="Pilot this tile"
          hint="Roll it out to part of the fleet first. Enterprise feature."
          value={false}
          onChange={(on) => {
            if (on) setPilot({ rule: { kind: 'percent', pct: 5 } });
          }}
        />
      </fieldset>
    );
  }

  const { rule, from, to } = pilot;

  // Rebuild the pilot, preserving the window. Ramps need a `from` anchor, so
  // default it to today if the window's start isn't set yet.
  const withRule = (nextRule: PilotRule): void => {
    const needsFrom = nextRule.kind === 'percentRamp';
    const f = needsFrom && from === undefined ? todayIso() : from;
    setPilot({
      ...(f !== undefined && { from: f }),
      ...(to !== undefined && { to }),
      rule: nextRule,
    });
  };
  const withWindow = (nextFrom: string | undefined, nextTo: string | undefined): void => {
    // A ramp can't lose its start anchor — re-default to today if cleared.
    const f = rule.kind === 'percentRamp' && nextFrom === undefined ? todayIso() : nextFrom;
    setPilot({
      ...(f !== undefined && { from: f }),
      ...(nextTo !== undefined && { to: nextTo }),
      rule,
    });
  };

  return (
    <fieldset className="tile-card__pilot">
      <legend className="tile-card__appearance-label">Pilot rollout</legend>
      <ToggleField
        label="Pilot this tile"
        hint="Enterprise feature — fleets on other plans show the tile to everyone."
        value
        onChange={(on) => {
          if (!on) setPilot(undefined);
        }}
      />

      <SelectField
        label="Targeting"
        value={
          rule.kind === 'percentRamp' || rule.kind === 'subnet' || rule.kind === 'tag'
            ? rule.kind
            : 'percent'
        }
        options={[
          { value: 'percent', label: 'Percentage of fleet' },
          { value: 'percentRamp', label: 'Ramp up over time' },
          { value: 'subnet', label: 'By subnet (LAN IP)' },
          { value: 'tag', label: 'By device tag' },
        ]}
        onChange={(k) => {
          if (k === 'percentRamp') {
            const startPct = rule.kind === 'percent' ? rule.pct : 5;
            withRule({ kind: 'percentRamp', startPct, perDay: 10 });
          } else if (k === 'subnet') {
            // Seed with the broadest observed subnet so the pilot is valid
            // immediately; admin refines from there.
            const first = subnets[0]?.cidr;
            withRule({ kind: 'subnet', cidrs: first !== undefined ? [first] : [] });
          } else if (k === 'tag') {
            const first = tags[0];
            withRule({ kind: 'tag', tags: first !== undefined ? [first] : [] });
          } else {
            const pct = rule.kind === 'percentRamp' ? rule.startPct : 5;
            withRule({ kind: 'percent', pct });
          }
        }}
      />

      <label className="field">
        <span className="field__label">Start date (optional)</span>
        <input
          className="field__input"
          type="date"
          value={dateInputValue(from)}
          onChange={(e) => withWindow(toIso(e.target.value), to)}
        />
      </label>
      <label className="field">
        <span className="field__label">End date (optional)</span>
        <input
          className="field__input"
          type="date"
          value={dateInputValue(to)}
          onChange={(e) => withWindow(from, toIso(e.target.value))}
        />
        <span className="field__hint">
          One day before this date the tile graduates to the whole fleet.
        </span>
      </label>

      {rule.kind === 'percent' && (
        <SelectField
          label="Share of fleet"
          value={String(rule.pct)}
          options={PCT_OPTIONS}
          onChange={(v) => withRule({ kind: 'percent', pct: Number(v) })}
        />
      )}

      {rule.kind === 'percentRamp' && (
        <>
          <SelectField
            label="Starting share"
            value={String(rule.startPct)}
            options={PCT_OPTIONS}
            onChange={(v) => withRule({ kind: 'percentRamp', startPct: Number(v), perDay: rule.perDay })}
          />
          <label className="field">
            <span className="field__label">Increase per day</span>
            <input
              className="field__input"
              type="number"
              min={1}
              max={100}
              value={rule.perDay}
              onChange={(e) =>
                withRule({
                  kind: 'percentRamp',
                  startPct: rule.startPct,
                  perDay: clamp(Number(e.target.value), 1, 100),
                })
              }
            />
          </label>
          <details className="tile-card__pilot-math">
            <summary>Rollout math</summary>
            <p>{rampHint(from, rule.startPct, rule.perDay, to)}</p>
          </details>
        </>
      )}

      {rule.kind === 'subnet' && (
        <SubnetPicker
          selected={rule.cidrs}
          options={subnets}
          onToggle={(cidr) => {
            const next = rule.cidrs.includes(cidr)
              ? rule.cidrs.filter((c) => c !== cidr)
              : [...rule.cidrs, cidr];
            withRule({ kind: 'subnet', cidrs: next });
          }}
        />
      )}

      {rule.kind === 'tag' && (
        <TagPicker
          selected={rule.tags}
          options={tags}
          onChange={(next) => withRule({ kind: 'tag', tags: next })}
        />
      )}
    </fieldset>
  );
}

/**
 * Pick device tags to target. Offers the tags already in use across the fleet
 * (from the Devices panel) as checkboxes, plus a free-text add so you can
 * pre-create a pilot for a tag you haven't assigned yet.
 */
function TagPicker({
  selected,
  options,
  onChange,
}: {
  selected: readonly string[];
  options: readonly string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const all = [...new Set([...options, ...selected])].sort((a, b) => a.localeCompare(b));
  const toggle = (tag: string): void => {
    onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag]);
  };
  const addDraft = (): void => {
    const t = draft.trim();
    setDraft('');
    if (t.length > 0 && !selected.includes(t)) onChange([...selected, t]);
  };

  return (
    <div className="tile-card__pilot-tags">
      {all.length > 0 && (
        <div className="tile-card__pilot-subnets" role="group" aria-label="Target tags">
          {all.map((tag) => (
            <label key={tag} className="tile-card__pilot-subnet">
              <input
                type="checkbox"
                checked={selected.includes(tag)}
                onChange={() => toggle(tag)}
              />
              <code>{tag}</code>
            </label>
          ))}
        </div>
      )}
      <div className="tile-card__pilot-tag-add">
        <input
          className="field__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addDraft();
            }
          }}
          placeholder="Add a tag…"
          maxLength={60}
          aria-label="Add a target tag"
        />
        <button type="button" className="tile-card__pilot-tag-btn" onClick={addDraft}>
          Add
        </button>
      </div>
      <p className="field__hint">
        Tag devices in the Devices tab; tagged devices then match this pilot.
      </p>
    </div>
  );
}

/**
 * Checkboxes for the subnet groupings derived from the fleet's live device
 * IPs (1st/2nd/3rd-octet prefixes). Any already-selected CIDR not in the
 * current options (a stale one, or a device that has since gone quiet) is
 * still shown checked so it can be removed.
 */
function SubnetPicker({
  selected,
  options,
  onToggle,
}: {
  selected: readonly string[];
  options: readonly { cidr: string; count: number }[];
  onToggle: (cidr: string) => void;
}): JSX.Element {
  const optionCidrs = new Set(options.map((o) => o.cidr));
  const rows = [
    ...options,
    ...selected.filter((c) => !optionCidrs.has(c)).map((cidr) => ({ cidr, count: 0 })),
  ];

  if (rows.length === 0) {
    return (
      <p className="field__hint">
        No LAN subnets seen yet — these come from each device's own LAN IPv4,
        which the kiosk app reports on check-in. Pick a date or percentage
        rollout for now.
      </p>
    );
  }

  return (
    <div className="tile-card__pilot-subnets" role="group" aria-label="Target subnets">
      {rows.map((o) => (
        <label key={o.cidr} className="tile-card__pilot-subnet">
          <input
            type="checkbox"
            checked={selected.includes(o.cidr)}
            onChange={() => onToggle(o.cidr)}
          />
          <code>{o.cidr}</code>
          {o.count > 0 && (
            <span className="tile-card__pilot-subnet-count">
              {o.count} device{o.count === 1 ? '' : 's'}
            </span>
          )}
        </label>
      ))}
    </div>
  );
}

/** Human-readable ramp projection, shown on demand under the ramp controls. */
function rampHint(
  from: string | undefined,
  startPct: number,
  perDay: number,
  to: string | undefined,
): string {
  if (from === undefined) return 'Set a start date to project the ramp.';
  const days = Math.max(0, Math.ceil((100 - startPct) / perDay));
  const reach = isoDay(Date.parse(from) + days * DAY_MS);
  let line = `Starts at ${String(startPct)}% on ${dateInputValue(from)}, +${String(perDay)}%/day → reaches 100% of the fleet in ~${String(days)} day${days === 1 ? '' : 's'} (${reach}).`;
  if (to !== undefined) {
    const graduate = isoDay(Date.parse(to) - DAY_MS);
    line += ` Graduates to everyone on ${graduate} (one day before the end date).`;
  }
  return line;
}
